import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Send, Sparkles, X, Mail, BookOpen, Headphones, Clock3, BadgePercent } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../api";
import { toast } from "sonner";

const GREETING = {
  role: "ai",
  text: "Cześć! Jestem Booki ✨ — Twój osobisty doradca książkowy. Powiedz mi, czego szukasz, a ja zajmę się resztą: znajdziemy KAŻDEGO ebooka lub audiobooka i do 24h przygotujemy ofertę z najkorzystniejszą ceną na rynku.",
  chips: ["Znam tytuł książki", "Szukam tematyki", "Doradź mi coś dobrego"],
};

const Orb = () => (
  <div className="relative h-10 w-10 shrink-0">
    <div className="absolute inset-0 animate-ping rounded-full bg-fuchsia-500/25" style={{ animationDuration: "2.6s" }} />
    <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 via-purple-500 to-cyan-400 shadow-lg shadow-fuchsia-500/40">
      <Sparkles className="h-5 w-5 text-white" />
    </div>
  </div>
);

const Typing = () => (
  <div className="flex items-center gap-1.5 px-4 py-3" data-testid="advisor-typing">
    {[0, 1, 2].map((i) => (
      <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-fuchsia-400" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </div>
);

export function BookRequestModal() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ready, setReady] = useState(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setDone(false);
      setReady(null);
      setEmail("");
      setInput("");
      setMessages([GREETING]);
    };
    window.addEventListener("open-book-request", handler);
    return () => window.removeEventListener("open-book-request", handler);
  }, []);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending, ready]);

  if (!open) return null;
  const close = () => setOpen(false);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const history = messages.map(({ role, text: t }) => ({ role: role === "ai" ? "ai" : "user", text: t }));
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/book-requests/chat", { message: msg, history });
      setMessages((m) => [...m, { role: "ai", text: data.answer || "Powiedz mi proszę trochę więcej — tytuł albo tematyka?", chips: data.chips || [] }]);
      if (data.ready) setReady(data.ready);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Przepraszam, chwilowa przerwa techniczna… napisz jeszcze raz za moment 💜", chips: ["Spróbuję jeszcze raz"] }]);
    } finally {
      setSending(false);
    }
  };

  const submit = async () => {
    if (!email.includes("@")) { toast.error("Podaj poprawny adres email"); return; }
    setSubmitting(true);
    try {
      await api.post("/book-requests", {
        email,
        title: ready?.title || "",
        topic: ready?.topic || "",
        format: ready?.format || "obojętnie",
        notes: ready?.notes || "",
        description: [ready?.title, ready?.topic, ready?.notes].filter(Boolean).join(" — "),
        transcript: messages.slice(-20).map(({ role, text }) => ({ role, text })),
      });
      setDone(true);
    } catch {
      toast.error("Nie udało się wysłać zgłoszenia. Spróbuj ponownie.");
    } finally {
      setSubmitting(false);
    }
  };

  const lastAiIdx = [...messages].map((m, i) => (m.role === "ai" ? i : -1)).filter((i) => i >= 0).pop();
  const fmtIcon = ready?.format === "audiobook" ? Headphones : BookOpen;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="book-request-modal">
      <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-sm" onClick={close} />
      <div className="glow-border relative w-full max-w-xl rounded-3xl p-[2px]">
        <div className="relative flex max-h-[88vh] flex-col overflow-hidden rounded-3xl bg-slate-950/95">
          {/* header */}
          <div className="relative flex items-center gap-3 border-b border-slate-800/80 bg-gradient-to-r from-fuchsia-500/10 via-transparent to-cyan-500/10 px-6 py-4">
            <Orb />
            <div className="min-w-0">
              <h2 className="font-display text-lg font-extrabold tracking-tight">
                Booki — <span className="gradient-text">Twój doradca książkowy</span>
              </h2>
              <p className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-300">
                <span className="relative flex h-2 w-2"><span className="absolute h-full w-full animate-ping rounded-full bg-teal-400 opacity-60" /><span className="h-2 w-2 rounded-full bg-teal-400" /></span>
                online · oferta do 24h
              </p>
            </div>
            <button onClick={close} data-testid="book-request-close" className="ml-auto rounded-full p-2 text-slate-400 transition-colors hover:text-white" aria-label="Zamknij">
              <X className="h-5 w-5" />
            </button>
          </div>

          {!done ? (
            <>
              {/* czat */}
              <div ref={bodyRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="advisor-chat" style={{ minHeight: 320 }}>
                <AnimatePresence initial={false}>
                  {messages.map((m, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.25 }}
                      className={`flex ${m.role === "user" ? "justify-end" : "items-start gap-3"}`}>
                      {m.role === "ai" && <Orb />}
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "rounded-br-md bg-gradient-to-r from-fuchsia-500 to-cyan-500 font-medium text-white shadow-lg shadow-fuchsia-500/20"
                          : "rounded-bl-md border border-slate-800 bg-slate-900/80 text-slate-200"}`}
                        data-testid={`advisor-msg-${m.role}`}>
                        {m.text}
                        {m.role === "ai" && i === lastAiIdx && m.chips?.length > 0 && !ready && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {m.chips.map((c, j) => (
                              <motion.button key={c} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + j * 0.08 }}
                                onClick={() => send(c)} data-testid={`advisor-chip-${j}`}
                                className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3.5 py-1.5 text-xs font-semibold text-cyan-200 transition-all hover:scale-105 hover:bg-cyan-400/25 active:scale-95">
                                {c}
                              </motion.button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {sending && <div className="flex items-start gap-3"><Orb /><div className="rounded-2xl rounded-bl-md border border-slate-800 bg-slate-900/80"><Typing /></div></div>}

                {/* karta podsumowania + email */}
                <AnimatePresence>
                  {ready && (
                    <motion.div initial={{ opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                      className="rounded-2xl border border-teal-400/30 bg-gradient-to-br from-teal-500/10 via-slate-900/60 to-cyan-500/10 p-5 shadow-[0_20px_60px_-20px_rgba(45,212,191,0.4)]"
                      data-testid="advisor-summary">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-teal-300">Twoje zgłoszenie</p>
                      <div className="mt-3 space-y-2 text-sm">
                        {ready.title && !["obojętnie", "obojętny", "ebook", "audiobook", "nieznany"].includes(ready.title.toLowerCase()) && (
                          <p className="flex items-center gap-2 text-slate-200"><BookOpen className="h-4 w-4 text-fuchsia-300" /> <b>{ready.title}</b></p>
                        )}
                        {ready.topic && <p className="flex items-center gap-2 text-slate-200"><Sparkles className="h-4 w-4 text-cyan-300" /> Tematyka: <b>{ready.topic}</b></p>}
                        <p className="flex items-center gap-2 text-slate-300">
                          {React.createElement(fmtIcon, { className: "h-4 w-4 text-teal-300" })} Format: {ready.format}
                        </p>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-bold">
                        <span className="flex items-center gap-1.5 rounded-full bg-teal-400/15 px-3 py-1.5 text-teal-200"><Clock3 className="h-3.5 w-3.5" /> Oferta do 24h</span>
                        <span className="flex items-center gap-1.5 rounded-full bg-fuchsia-400/15 px-3 py-1.5 text-fuchsia-200"><BadgePercent className="h-3.5 w-3.5" /> Najkorzystniejsza cena na rynku</span>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <div className="relative flex-1">
                          <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                          <input data-testid="book-request-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="Twój email — tu wyślemy ofertę"
                            className="w-full rounded-xl border border-slate-700 bg-slate-900/80 py-3 pl-10 pr-4 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-teal-400" />
                        </div>
                        <button onClick={submit} disabled={submitting} data-testid="book-request-submit"
                          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-400 to-cyan-500 px-5 text-sm font-bold text-slate-950 shadow-lg shadow-teal-500/25 transition-transform hover:scale-105 active:scale-95 disabled:opacity-50">
                          <Send className="h-4 w-4" /> {submitting ? "Wysyłanie…" : "Wyślij"}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* input */}
              <div className="border-t border-slate-800/80 bg-slate-950/80 p-4">
                <div className="flex gap-2">
                  <input
                    data-testid="advisor-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Napisz tytuł albo opisz tematykę…"
                    className="flex-1 rounded-full border border-slate-700 bg-slate-900/80 px-5 py-3 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-fuchsia-500"
                  />
                  <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="advisor-send"
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white shadow-lg shadow-fuchsia-500/30 transition-transform hover:scale-110 active:scale-95 disabled:opacity-40">
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-2 text-center text-[10.5px] text-slate-500">Booki doradza i dopytuje — jak prawdziwy opiekun. Oferta z ceną trafia na Twój email do 24h.</p>
              </div>
            </>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} className="px-8 py-12 text-center" data-testid="book-request-success">
              <div className="relative mx-auto h-20 w-20">
                <div className="absolute inset-0 animate-ping rounded-full bg-teal-400/25" style={{ animationDuration: "2.2s" }} />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 shadow-xl shadow-teal-500/40">
                  <CheckCircle2 className="h-10 w-10 text-white" />
                </div>
              </div>
              <h2 className="mt-6 font-display text-2xl font-extrabold">Jesteś pod naszą opieką! 💜</h2>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                Booki przekazał Twoje zgłoszenie zespołowi DigiLib.store. Do <span className="font-semibold text-teal-300">24 godzin</span> wyślemy na
                <span className="font-semibold text-cyan-300"> {email}</span> propozycje z <span className="font-semibold text-fuchsia-300">najkorzystniejszą ceną na rynku</span>.
              </p>
              <button onClick={close} data-testid="book-request-done-close" className="mt-7 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 px-8 py-3 font-display text-sm font-bold text-white transition-transform hover:scale-105">
                Czekam na ofertę!
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
